import { Button, Grid, styled } from "@mui/material";
import AppModal from "components/AppModal";
import FlexBox from "components/flexbox/FlexBox";
import AppTextField from "components/input-fields/AppTextField";
import Scrollbar from "components/ScrollBar";
import { H2, H6 } from "components/Typography";
import { useEffect, useState } from "react"; // Added useEffect
import toast from "react-hot-toast";
import { API_ENDPOINTS } from "Network/apiEndpoints"; // Import API_ENDPOINTS
import apiService from "Network/apiService"; // Import apiService
import * as Yup from "yup";

// styled components
const StyledAppModal = styled(AppModal)(({
  theme
}) => ({
  maxWidth: 450,
  minWidth: 200,
  [theme.breakpoints.down(325)]: {
    maxWidth: "100%"
  }
}));

const AddDistrictModal = ({
  open,
  onClose,
  edit,
  data,
  onSuccess
}) => {
  // Initialize state variables
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Set form data when editing
  useEffect(() => {
    if (edit && data) {
      setName(data.name || "");
      setDescription(data.description || "");
    } else {
      // Reset form when not editing
      setName("");
      setDescription("");
    }
  }, [edit, data]);

  // Validation schema (using Yup, as in your original code)
  const fieldValidationSchema = Yup.object().shape({
    name: Yup.string().min(3, "Too Short").required("Name is Required!"),
    description: Yup.string().min(3, "Too Short").required("Description is Required!"),
  });

  // Function to handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await fieldValidationSchema.validate({ name, description }, { abortEarly: false });

      const payload = {
        name,
        description,
        user: JSON.parse(localStorage.getItem("logged_data") || "{}")?.user?.id,  

      };

      if (edit) {
        payload.district_id = data.id;
      }

      const response = await apiService[edit ? 'put' : 'post'](
        edit ? API_ENDPOINTS.PUT_update_district : API_ENDPOINTS.POST_create_district,
        payload
      );

      if (response?.success) {
        toast.success(`District ${edit ? 'updated' : 'created'} successfully!`);
        await onSuccess?.(); // Wait for refresh to complete
        onClose();
        setName("");
        setDescription("");
      } else {
        toast.error(response?.message || `Failed to ${edit ? 'update' : 'create'} district.`);
      }
    } catch (error) {
      // Handle validation errors
      if (error instanceof Yup.ValidationError) {
        // Map Yup validation errors to an object that Formik can use
        const validationErrors = {};
        error.inner.forEach(err => {
          validationErrors[err.path] = err.message;
        });
        toast.error("Please fill all required fields correctly.");
        console.error("Validation errors:", validationErrors); // Log the errors for debugging
      } else {
        console.error("API error:", error); // Log the full error object for debugging
        toast.error("An unexpected error occurred.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StyledAppModal open={open} handleClose={onClose}>
      <H2 mb={2}>{edit ? "Edit District" : "Add new District"}</H2>

      <form onSubmit={handleSubmit}>
        <Scrollbar style={{ maxHeight: 400 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <H6 mb={1}>District Name</H6>
              <AppTextField
                fullWidth
                size="small"
                name="name"
                placeholder="District Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isLoading}
              />
            </Grid>

            <Grid item xs={6}>
              <H6 mb={1}>Description</H6>
              <AppTextField
                fullWidth
                size="small"
                name="description"
                placeholder="District Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isLoading}
              />
            </Grid>
          </Grid>
        </Scrollbar>

        <FlexBox justifyContent="flex-end" gap={2} marginTop={4}>
          <Button 
            fullWidth 
            size="small" 
            variant="outlined" 
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            fullWidth 
            size="small" 
            type="submit" 
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : (edit ? "Update" : "Save")}
          </Button>
        </FlexBox>
      </form>
    </StyledAppModal>
  );
};

export default AddDistrictModal;
